import {useAgentInfo} from './use-agent-info'
import {useLatestAgentModel} from './use-latest-agent-model'
import {getAgentModelVendors} from '../utils/agent-helpers'
import type {SelectedAgent} from '../types/session'

/**
 * Resolves the model vendors and persisted model selection for a given agent.
 *
 * This hook encapsulates the common pattern of:
 * 1. Reading the vendors directly from the SelectedAgent (set when the user picks an agent from the list)
 * 2. Falling back to the server-provided `model_vendors` from AgentInfo (for enrichment/legacy paths)
 * 3. Managing the persisted per-vendor-set model selection in localStorage
 */
export function useAgentVendorModel(selectedAgent: SelectedAgent) {
  const {data: agentInfo, isLoading: isAgentInfoLoading} = useAgentInfo(
    selectedAgent.topLevelAgentId ? Number(selectedAgent.topLevelAgentId) : null,
  )
  const agentSlug = agentInfo?.slug ?? selectedAgent.login
  const hasSelectedAgentVendors = selectedAgent.modelVendors !== undefined
  const needsVendorEnrichment = !!selectedAgent.topLevelAgentId && !hasSelectedAgentVendors
  const isVendorScopeLoading = needsVendorEnrichment && isAgentInfoLoading

  // Prefer vendors from SelectedAgent (set at selection time from the list endpoint),
  // fall back to server-provided model_vendors from the individual agent info endpoint.
  const resolvedModelVendors = hasSelectedAgentVendors ? selectedAgent.modelVendors : agentInfo?.model_vendors
  const vendorFilters = getAgentModelVendors(resolvedModelVendors)
  const [selectedModelId, setSelectedModelId] = useLatestAgentModel(vendorFilters)

  return {agentSlug, vendorFilters, selectedModelId, setSelectedModelId, isVendorScopeLoading}
}
